/**
 * One day of the practice's rooms, decided without React and without the
 * network. A column per operatory, named as the PMS names it, holding that
 * room's booked work and its open time. Nothing here infers what may be booked
 * into a slot. See docs/superpowers/specs/2026-08-14-schedule-week-design.md.
 */

import { packLanes } from 'lib/dayClock'
import { Minutes, slotMinutes } from './scheduleGrid'

/** One booked appointment, as the PMS has it. Both times are wall clock. */
export type DayAppointment = {
  id: string
  /** `"14:00"`, wall clock. */
  startTime: string
  endTime: string
  operatoryId: string
  description?: string | null
  cancelled?: boolean | null
  broken?: boolean | null
  confirmed?: boolean | null
}

/**
 * A stretch of a room's day with nobody in it: the room's own hours minus what
 * is booked and blocked, not the smaller set the scheduler offers.
 */
export type PlacedOpening = {
  start: Minutes
  end: Minutes
}

export type PlacedAppointment = {
  appointment: DayAppointment
  start: Minutes
  end: Minutes
  /** Which overlap lane inside the room, for double-booked chairs. */
  lane: number
  /** How many lanes this room needs at its busiest. */
  lanes: number
}

/**
 * - `open`: staffed today, with time nobody has bought yet.
 * - `full`: staffed today and every minute of it sold.
 * - `closed`: no schedule and no work, which is the PMS saying they are shut.
 * - `unknown`: no published hours but work booked in the room, so the hours
 *   have not reached us. Calling that shut would tell a working practice it is
 *   closed and report nought open hours as though it were a finding.
 */
export type ColumnState = `open` | `full` | `closed` | `unknown`

export type DayColumn = {
  operatoryId: string
  /** The PMS name for the room: "Dr. Hamblin (Major)", "Carly". */
  label: string
  openings: PlacedOpening[]
  appointments: PlacedAppointment[]
  state: ColumnState
  /** Minutes of this room's day that are already booked. */
  bookedMinutes: number
  /** Minutes of this room's working day that nobody is in. */
  freeMinutes: number
  /** Minutes the room is staffed for at all. */
  workingMinutes: number
}

export type Day = {
  date: string
  columns: DayColumn[]
  startMinute: Minutes
  endMinute: Minutes
  /** Rooms the practice is running today, of however many the PMS lists. */
  workingCount: number
  totalOpenings: number
  freeMinutes: number
  /** A room is being worked without published hours, so `freeMinutes` is a floor. */
  hoursMissing: boolean
  totalBooked: number
}

const DEFAULT_DURATION = 30
const DEFAULT_START = 8 * 60
const DEFAULT_END = 17 * 60

/**
 * The treatment board's rule: a cancelled appointment is not booked. A chair
 * somebody just cancelled out of is the most fillable time the practice has.
 */
export function occupiesChair(appointment: DayAppointment): boolean {
  return !appointment.cancelled && !appointment.broken
}

/**
 * A room's booked work, packed into as few lanes as its overlaps need. Chairs
 * get double-booked: a hygienist runs the appointment and the dentist drops in
 * for the exam, and stacking those would hide one of them.
 */
function placeAppointments(appointments: DayAppointment[]): PlacedAppointment[] {
  const spans = appointments.flatMap((appointment) => {
    if (!occupiesChair(appointment)) return []
    const start = slotMinutes(appointment.startTime)
    const end = slotMinutes(appointment.endTime)
    if (start === undefined) return []
    return [{ appointment, start, end: end !== undefined && end > start ? end : start + DEFAULT_DURATION }]
  })
  return packLanes(spans)
}

/**
 * The drawn window: a working day at minimum, widened to the real day. The
 * minimum keeps one day comparable with the next, rather than zooming a quiet
 * Tuesday until its single eleven o'clock fills the screen.
 */
export function dayWindow(columns: DayColumn[]): { startMinute: Minutes; endMinute: Minutes } {
  const placed = [...columns.flatMap((c) => c.openings), ...columns.flatMap((c) => c.appointments)]
  if (!placed.length) return { startMinute: DEFAULT_START, endMinute: DEFAULT_END }
  const start = Math.min(Math.floor(Math.min(...placed.map((p) => p.start)) / 60) * 60, DEFAULT_START)
  const end = Math.max(Math.ceil(Math.max(...placed.map((p) => p.end)) / 60) * 60, DEFAULT_END)
  return { startMinute: start, endMinute: end }
}

/** A span of a room's day, in minutes past midnight. */
export type Interval = { start: Minutes; end: Minutes }

/**
 * A room's working day as the PMS has it. `blocks` is when the room is staffed
 * and there can be several (nine to one, two to four, lunch in between);
 * `blocked` is time the practice reserved inside those hours. No record for a
 * date means the room is not working that day.
 */
export type RoomSchedule = {
  operatoryId: string
  blocks: Interval[]
  blocked?: Interval[]
}

/**
 * What is left of a room's day once booked and blocked time is taken out. Gaps
 * shorter than `minimum` are dropped: eight minutes between two crowns is
 * arithmetic, not an opening, and drawing it would bury the real ones.
 */
export function freeIntervals(schedule: RoomSchedule, busy: Interval[], minimum: Minutes): Interval[] {
  const taken = [...busy, ...(schedule.blocked ?? [])].sort((a, b) => a.start - b.start)
  const free: Interval[] = []
  for (const block of schedule.blocks) {
    let cursor = block.start
    for (const span of taken) {
      if (span.end <= cursor || span.start >= block.end) continue
      if (span.start > cursor) free.push({ start: cursor, end: span.start })
      cursor = Math.max(cursor, span.end)
    }
    if (cursor < block.end) free.push({ start: cursor, end: block.end })
  }
  return free.filter((gap) => gap.end - gap.start >= minimum)
}

/**
 * The next day the practice is actually open, so a day with nothing to sell is
 * not a dead end clicked out of one day at a time.
 */
export function nextWorkingDate(dates: string[], after: string): string | undefined {
  return dates.filter((date) => date > after).sort()[0]
}

/** The last day we have schedules for, so the board can say how far it looked. */
export function horizonEnd(dates: string[]): string | undefined {
  return [...dates].sort().pop()
}

/**
 * `operatories` drives the columns rather than the data, so a room with nothing
 * in it still gets drawn and still says its name, in the order the PMS lists it.
 */
export function buildDay(input: {
  date: string
  appointments?: DayAppointment[]
  /** The location's rooms, in the order the PMS lists them. */
  operatories: { operatoryId: string; label: string }[]
  /** Each room's working hours for this date, as the PMS has them. */
  schedules?: RoomSchedule[]
  /** The shortest gap worth offering. The practice's own slot length, when known. */
  minimumGap?: Minutes
}): Day {
  const booked = input.appointments ?? []
  const scheduleFor = new Map((input.schedules ?? []).map((schedule) => [schedule.operatoryId, schedule]))
  const minimum = input.minimumGap && input.minimumGap > 0 ? input.minimumGap : DEFAULT_DURATION

  const columns: DayColumn[] = input.operatories.map((room) => {
    const appointments = placeAppointments(booked.filter((a) => String(a.operatoryId) === room.operatoryId))
    const schedule = scheduleFor.get(room.operatoryId)
    // Both halves of a double-booked chair count against the room, so gaps come
    // out of what is really left rather than out of one lane of it.
    const busy = appointments.map((block) => ({ start: block.start, end: block.end }))
    const free = schedule ? freeIntervals(schedule, busy, minimum) : []
    const working = Boolean(schedule?.blocks.length)
    return {
      operatoryId: room.operatoryId,
      label: room.label,
      openings: free.map((gap) => ({ start: gap.start, end: gap.end })),
      appointments,
      bookedMinutes: appointments.reduce((total, block) => total + (block.end - block.start), 0),
      freeMinutes: free.reduce((total, gap) => total + (gap.end - gap.start), 0),
      workingMinutes: (schedule?.blocks ?? []).reduce((total, block) => total + (block.end - block.start), 0),
      state: working ? (free.length ? `open` : `full`) : appointments.length ? `unknown` : `closed`,
    }
  })

  return {
    date: input.date,
    columns,
    ...dayWindow(columns),
    workingCount: columns.filter((column) => column.state !== `closed`).length,
    totalOpenings: columns.reduce((total, column) => total + column.openings.length, 0),
    freeMinutes: columns.reduce((total, column) => total + column.freeMinutes, 0),
    totalBooked: columns.reduce((total, column) => total + column.appointments.length, 0),
    hoursMissing: columns.some((column) => column.state === `unknown`),
  }
}
